import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Activity, DollarSign, FileSpreadsheet, UserPlus, Users } from "lucide-react";
import { Badge } from "../components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ActivityLogsPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    try {
      const response = await axios.get(`${API}/activity-logs`);
      setLogs(response.data);
    } catch (error) {
      toast.error("Failed to fetch activity logs");
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (action) => {
    if (action.includes("FEE")) return <DollarSign size={14} className="text-emerald-500" />;
    if (action.includes("RESULT")) return <FileSpreadsheet size={14} className="text-blue-500" />;
    if (action.includes("USER") || action.includes("TEACHER")) return <UserPlus size={14} className="text-pink-500" />;
    return <Activity size={14} className="text-slate-500" />;
  };

  const getActionColor = (action) => {
    if (action.includes("FEE")) return "bg-emerald-50 text-emerald-700";
    if (action.includes("RESULT")) return "bg-blue-50 text-blue-700";
    if (action.includes("CREATE")) return "bg-pink-50 text-pink-700";
    return "bg-slate-50 text-slate-700";
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case "admin":
        return "bg-pink-50 text-pink-700";
      case "teacher":
        return "bg-blue-50 text-blue-700";
      default:
        return "bg-slate-50 text-slate-700";
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };

  // Filter to show only teacher activities and important admin actions
  const teacherLogs = logs.filter(log => log.role === "teacher" || log.action.includes("TEACHER"));

  return (
    <div data-testid="activity-logs-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="font-heading font-bold text-2xl text-slate-900 flex items-center gap-3">
          <Activity className="text-pink-500" size={28} />
          Activity Logs
        </h1>
        <p className="text-slate-500 mt-1 text-sm">
          Track teacher activities on the portal
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <p className="text-2xl font-bold text-slate-900">{logs.length}</p>
            <p className="text-sm text-slate-500">Total Activities</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <p className="text-2xl font-bold text-emerald-600">
              {logs.filter(l => l.action.includes("FEE")).length}
            </p>
            <p className="text-sm text-slate-500">Fee Updates</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <p className="text-2xl font-bold text-blue-600">
              {logs.filter(l => l.action.includes("RESULT")).length}
            </p>
            <p className="text-sm text-slate-500">Results Entered</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <p className="text-2xl font-bold text-pink-600">
              {teacherLogs.length}
            </p>
            <p className="text-sm text-slate-500">Teacher Actions</p>
          </CardContent>
        </Card>
      </div>

      {/* All Logs */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-lg">
            All Activity Logs ({logs.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-500"></div>
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Activity size={48} className="mx-auto mb-4 opacity-50" />
              <p>No activity logs yet</p>
              <p className="text-sm">Activities will appear here as users interact with the portal</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 hover:bg-slate-50">
                    <TableHead className="font-medium text-slate-500">Timestamp</TableHead>
                    <TableHead className="font-medium text-slate-500">User</TableHead>
                    <TableHead className="font-medium text-slate-500">Role</TableHead>
                    <TableHead className="font-medium text-slate-500">Action</TableHead>
                    <TableHead className="font-medium text-slate-500">Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log) => (
                    <TableRow
                      key={log.id}
                      className="table-row-hover"
                      data-testid={`log-row-${log.id}`}
                    >
                      <TableCell className="text-slate-500 text-sm whitespace-nowrap">
                        {formatTimestamp(log.created_at)}
                      </TableCell>
                      <TableCell className="font-medium text-slate-900">
                        {log.username}
                      </TableCell>
                      <TableCell>
                        <Badge className={getRoleBadgeColor(log.role)}>
                          {log.role}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={getActionColor(log.action)}>
                          <span className="flex items-center gap-1">
                            {getActionIcon(log.action)}
                            {log.action.replace("_", " ")}
                          </span>
                        </Badge>
                      </TableCell>
                      <TableCell className="text-slate-600 text-sm max-w-md truncate">
                        {log.details}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
